import React, { useId, useRef, useState } from "react";
import Select from "react-select";
import { profileCountries } from "../data/profileCountries";
import {
  Answer,
  Answers,
  Field,
  FormSchema,
  Row,
  Upload,
  formError,
} from "./types";

const countryChoices = profileCountries.filter((country) =>
  /^[A-Z]{2}$/.test(country.code),
);

function validTermsUrl(value?: string) {
  if (!value || !/^https?:\/\//i.test(value)) return;
  try {
    return new URL(value).href;
  } catch {
    return;
  }
}

export function TermsLink({ field }: { field: Field }) {
  const url = validTermsUrl(field.termsUrl);
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {field.termsLinkText || "Read the college’s terms and conditions"}
      <span className="visually-hidden"> (opens in a new tab)</span>
    </a>
  );
}

export function applicable(field: Field, values: Answers | Row) {
  if (!field.when) return true;
  const { fieldId, value: expected, aliases = [] } = field.when;
  const value = values[fieldId];
  return (
    typeof value === "string" &&
    [expected, ...aliases].some(
      (candidate) =>
        value.trim().toLowerCase() === candidate.trim().toLowerCase(),
    )
  );
}

function FieldInput({
  field,
  value,
  onChange,
  upload,
  download,
  files,
  disabled,
}: {
  field: Field;
  value: Answer | undefined;
  onChange: (value: Answer | undefined) => void;
  upload?: (fieldId: string, file: File, requestId: string) => Promise<Upload>;
  download?: (file: Upload) => Promise<void>;
  files: Upload[];
  disabled?: boolean;
}) {
  const id = useId();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const lastUpload = useRef<{ identity: string; id: string }>();
  const name = typeof value === "string" ? value : "";
  const selected = files.find((file) => file.id === value);
  const description =
    field.description ||
    (field.type === "file" && field.maxBytes
      ? `Up to ${field.maxBytes / 1048576} MB`
      : "");
  const isBoolean = field.type === "checkbox";
  const termsUrl = isBoolean ? validTermsUrl(field.termsUrl) : undefined;
  const describedBy =
    [description && `${id}-help`, termsUrl && `${id}-terms`, error && `${id}-error`]
      .filter(Boolean)
      .join(" ") || undefined;
  const label = (
    <>
      {field.label}
      {field.required && <span aria-hidden="true"> *</span>}
    </>
  );
  const control = {
    id,
    disabled,
    "aria-describedby": describedBy,
    "aria-required": field.required || undefined,
  };
  let input: React.ReactNode;
  if (field.type === "termsAgreement") {
    return (
      <div className="mb-3">
        <span>{field.label}</span>{" "}
        <TermsLink field={field} />
        {description && <div className="form-text">{description}</div>}
      </div>
    );
  }
  if (field.type === "textarea")
    input = (
      <textarea
        {...control}
        className="form-control"
        rows={3}
        value={name}
        placeholder={field.placeholder}
        minLength={field.min}
        maxLength={field.max}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  else if (field.type === "select" || field.type === "country") {
    const options =
      field.type === "country"
        ? countryChoices.map((country) => ({
            id: country.code,
            label: country.name,
          }))
        : field.options;
    input = (
      <Select
        inputId={id}
        instanceId={id}
        aria-required={field.required || undefined}
        isDisabled={disabled}
        isSearchable
        isClearable
        menuPlacement="auto"
        options={options}
        getOptionValue={(option) => option.id}
        value={options?.find((option) => option.id === name) ?? null}
        placeholder={field.placeholder || "Select an option"}
        onChange={(option) => onChange(option?.id)}
      />
    );
  } else if (field.type === "radio" || field.type === "multiSelect") {
    return (
      <fieldset
        className="mb-3"
        aria-describedby={describedBy}
        disabled={disabled}
      >
        <legend className="form-label fw-semibold fs-6">{label}</legend>
        {description && (
          <p id={`${id}-help`} className="form-text mt-0">
            {description}
          </p>
        )}
        <div className="d-flex flex-wrap column-gap-3 row-gap-2">
          {field.options?.map((option) => {
            const multi = field.type === "multiSelect";
            const values = Array.isArray(value) ? value : [];
            return (
              <label className="form-check mb-0" key={option.id}>
                <input
                  className="form-check-input"
                  type={multi ? "checkbox" : "radio"}
                  name={id}
                  checked={
                    multi ? values.includes(option.id) : name === option.id
                  }
                  onChange={(event) =>
                    onChange(
                      multi
                        ? event.target.checked
                          ? [...values, option.id]
                          : values.filter((item) => item !== option.id)
                        : option.id,
                    )
                  }
                />
                <span className="form-check-label text-break">
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  } else if (isBoolean) {
    input = (
      <div className="form-check">
        <input
          {...control}
          className="form-check-input"
          type="checkbox"
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
        <label htmlFor={id} className="form-check-label fw-semibold">
          {label}
        </label>
        {termsUrl && (
          <div id={`${id}-terms`} className="form-text">
            <a href={termsUrl} target="_blank" rel="noopener noreferrer">
              Read the college’s terms and conditions (opens in a new tab)
            </a>
          </div>
        )}
      </div>
    );
  } else if (field.type === "file") {
    input = (
      <>
        <input
          {...control}
          disabled={disabled || busy}
          className="form-control"
          type="file"
          accept={field.mimeTypes?.join(",")}
          onChange={async (event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            setError("");
            if (field.maxBytes && file.size > field.maxBytes) {
              setError("This file exceeds the size limit");
              return;
            }
            if (!upload) {
              onChange(file.name);
              return;
            }
            const identity = JSON.stringify([
              file.name,
              file.size,
              file.lastModified,
              file.type,
            ]);
            if (lastUpload.current?.identity !== identity)
              lastUpload.current = { identity, id: crypto.randomUUID() };
            setBusy(true);
            try {
              onChange(
                (await upload(field.id, file, lastUpload.current.id)).id,
              );
              lastUpload.current = undefined;
            } catch (problem) {
              setError(formError(problem));
            } finally {
              setBusy(false);
            }
          }}
        />
        {busy && (
          <span role="status" className="form-text">
            Uploading…
          </span>
        )}
        {name && (
          <div className="d-flex gap-2 flex-wrap align-items-center mt-2">
            <span>
              {selected?.filename || (!upload ? name : "Uploaded file")}
            </span>
            {selected && download && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  void download(selected).catch((problem) =>
                    setError(formError(problem)),
                  )
                }
              >
                Download
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={disabled || busy}
              onClick={() => onChange(undefined)}
            >
              Remove
            </button>
          </div>
        )}
      </>
    );
  } else {
    const number = field.type === "number";
    const type = field.type === "phone" ? "tel" : field.type;
    input = (
      <input
        {...control}
        type={type}
        className="form-control"
        placeholder={field.placeholder}
        min={number ? field.min : undefined}
        max={number ? field.max : undefined}
        step={number ? "any" : undefined}
        minLength={number ? undefined : field.min}
        maxLength={number ? undefined : field.max}
        value={typeof value === "number" ? value : name}
        onChange={(event) =>
          onChange(
            number
              ? event.target.value === ""
                ? undefined
                : event.target.valueAsNumber
              : event.target.value,
          )
        }
      />
    );
  }
  return (
    <div className="mb-3">
      {!isBoolean && (
        <label htmlFor={id} className="form-label fw-semibold">
          {label}
        </label>
      )}
      {input}
      {description && (
        <div className="form-text" id={`${id}-help`}>
          {description}
        </div>
      )}
      {error && (
        <div role="alert" className="text-danger small" id={`${id}-error`}>
          {error}
        </div>
      )}
    </div>
  );
}

function change<T extends Answers | Row>(
  values: T,
  id: string,
  value: Answer | Row[] | undefined,
): T {
  const next = { ...values } as Answers;
  if (value === undefined) delete next[id];
  else next[id] = value;
  return next as T;
}

export function ApplicationFormFields({
  schema,
  sectionId,
  values,
  onChange,
  upload,
  download,
  files = [],
  disabled,
}: {
  schema: FormSchema;
  sectionId?: string;
  values: Answers;
  onChange: (values: Answers) => void;
  upload?: (fieldId: string, file: File, requestId: string) => Promise<Upload>;
  download?: (file: Upload) => Promise<void>;
  files?: Upload[];
  disabled?: boolean;
}) {
  const renderFields = (
    fields: Field[],
    row: Row | Answers,
    set: (id: string, value: Answer | undefined) => void,
  ) => (
    <div className="row gx-3">
      {fields
        .filter((field) => applicable(field, row))
        .map((field) => {
          const wide =
            field.type === "textarea" ||
            field.type === "file" ||
            field.type === "multiSelect" ||
            (field.type === "radio" && (field.options?.length ?? 0) > 3);
          return (
            <div className={wide ? "col-12" : "col-12 col-md-6"} key={field.id}>
              <FieldInput
                field={field}
                value={row[field.id] as Answer | undefined}
                onChange={(value) => set(field.id, value)}
                upload={upload}
                download={download}
                files={files}
                disabled={disabled}
              />
            </div>
          );
        })}
    </div>
  );
  const sections =
    sectionId === undefined
      ? schema.sections
      : schema.sections.filter((section) => section.id === sectionId);
  const uploadGroups =
    sectionId === undefined
      ? schema.uploadGroups
      : schema.uploadGroups.filter((group) =>
          sections.some((section) =>
            section.fields.some((field) => group.fieldIds.includes(field.id)),
          ),
        );
  return (
    <>
      {sections.map((section) => {
        const rows = section.repeat
          ? (values[section.id] as Row[] | undefined) || []
          : null;
        return (
          <section className="card mb-3" key={section.id}>
            <div className="card-body">
              <h2 className="h5 mb-3">{section.label}</h2>
              {section.repeat && rows ? (
                <>
                  <p className="form-text">
                    {section.repeat.min
                      ? `Add at least ${section.repeat.min} and up to ${section.repeat.max} entries.`
                      : `Add up to ${section.repeat.max} entries.`}
                  </p>
                  {rows.map((row, index) => (
                    <fieldset key={index} className="border rounded p-3 mb-3">
                      <legend className="h6 float-none w-auto px-1">
                        Entry {index + 1}
                      </legend>
                      {renderFields(section.fields, row, (id, value) =>
                        onChange(
                          change(
                            values,
                            section.id,
                            rows.map((entry, i) =>
                              i === index ? change(entry, id, value) : entry,
                            ),
                          ),
                        ),
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={disabled}
                        onClick={() =>
                          onChange(
                            change(
                              values,
                              section.id,
                              rows.filter((_entry, i) => i !== index),
                            ),
                          )
                        }
                      >
                        Remove entry {index + 1}
                      </button>
                    </fieldset>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={disabled || rows.length >= section.repeat.max}
                    onClick={() =>
                      onChange(change(values, section.id, [...rows, {}]))
                    }
                  >
                    Add entry
                  </button>
                </>
              ) : (
                renderFields(section.fields, values, (id, value) =>
                  onChange(change(values, id, value)),
                )
              )}
            </div>
          </section>
        );
      })}
      {uploadGroups.length > 0 && (
        <section className="card mb-3">
          <div className="card-body">
            <h2 className="h5">Document requirements</h2>
            <ul className="mb-0">
              {uploadGroups.map((group) => (
                <li key={group.id}>
                  {group.label} ({group.required ? "required" : "optional"}).
                  Upload any one of{" "}
                  {group.fieldIds
                    .map(
                      (id) =>
                        schema.sections
                          .flatMap((section) => section.fields)
                          .find((field) => field.id === id)?.label || id,
                    )
                    .join(", ")}
                  .
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </>
  );
}
